import { useRef, useState } from 'react';
import { Minimize } from '@mui/icons-material';
import { Avatar, Chip, ClickAwayListener, ListItem, ListItemAvatar, ListItemText, MenuItem, Select } from '@mui/material';
import { MuscleIntensity } from '@/generated';
import { Intensity } from '@/models/intensity';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { useMuscleTagsQuery } from '@/hooks/query-hooks';
import { useNavigate } from 'react-router-dom';
import { pathMap } from '@/navigation/paths';
import { ItemPreviewTooltip } from '@/components/ItemPreviewTooltip';

interface TagIntensityProps {
  muscleIntensity: MuscleIntensity;
  bgColor?: string;
  editable?: boolean;
  elementClicked?: (bzti: MuscleIntensity, deleted?: boolean) => void;
}

export const TagIntensity = (props: TagIntensityProps) => {
  const enumToArray = () =>
    Object.values(Intensity)
      .filter((key) => !isNaN(Number(key)))
      .map((k) => Number(k));
  const [editMode, setEditMode] = useState<boolean | undefined>(undefined);
  const elementRef = useRef<HTMLHeadingElement | null>(null);
  const { muscleTagMap } = useMuscleTagsQuery();
  return (
    <span ref={elementRef}>
      <ClickAwayListener
        onClickAway={(event) => {
          if ((event.target as any).localName === 'body') {
            event.preventDefault();
            return;
          }
          setEditMode(false);
        }}
      >
        {editMode ? (
          <Chip
            label={
              <>
                {muscleTagMap?.get(props.muscleIntensity.muscleId)?.name}
                {' - '}
                <Select variant="standard" sx={{ my: 1 }} value={props.muscleIntensity.intensity} displayEmpty>
                  {enumToArray()?.map((c) => (
                    <MenuItem
                      key={c}
                      value={c}
                      selected={props.muscleIntensity.intensity === c}
                      onClick={(e) => {
                        if (props.elementClicked) props.elementClicked({ ...props.muscleIntensity, intensity: c as any });
                        setEditMode(false);
                        e.stopPropagation();
                      }}
                    >
                      {Intensity[c]}
                    </MenuItem>
                  ))}
                </Select>
                <Minimize
                  sx={{ ml: 5 }}
                  onClick={(e) => {
                    if (props.elementClicked) props.elementClicked(props.muscleIntensity, true);
                    setEditMode(false);
                  }}
                />
              </>
            }
            style={{ backgroundColor: props.bgColor ?? '#606C38', fontSize: 14 }}
            onClick={() => {
              setEditMode(false);
            }}
          ></Chip>
        ) : (
          <Chip
            style={{ margin: 2, backgroundColor: props.bgColor ?? '#606C38', fontSize: 14 }}
            onClick={() => {
              if (props.editable) {
                setEditMode(!editMode);
              }
              if (props.elementClicked) props.elementClicked(props.muscleIntensity);
            }}
            label={`${muscleTagMap?.get(props.muscleIntensity.muscleId)?.name} - ${Intensity[props.muscleIntensity.intensity]}`}
          />
        )}
      </ClickAwayListener>
    </span>
  );
};

export const MuscleIntensityListItem = (props: { muscleIntensity: MuscleIntensity }) => {
  const { muscleIntensity } = props;
  const { muscleTags } = useMuscleTagsQuery();
  const wh = 35;
  const muscleTag = muscleTags?.find((mt) => mt.id === muscleIntensity.muscleId);
  const navigate = useNavigate();

  const content = (
    <ListItem
      onClick={() => navigate(`/${pathMap.BodyZones}?muscleTagId=${muscleTag?.id}`)}
      sx={{
        width: 250,
        flex: '0 0 auto',
        cursor: 'pointer',
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ width: wh, height: wh, backgroundColor: 'white' }}>
          <ImageFromServer sx={{ maxWidth: wh, maxHeight: wh }} thumbnail={true} alt="" id={muscleTag?.imageId} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText>
        {`${muscleTag?.name}`} - {`${Intensity[muscleIntensity.intensity]}`}
      </ListItemText>
    </ListItem>
  );

  return muscleTag ? (
    <ItemPreviewTooltip name={muscleTag.name} description={muscleTag.description} imageId={muscleTag.imageId}>
      {content}
    </ItemPreviewTooltip>
  ) : (
    content
  );
};
